import { useTranslation } from 'react-i18next';
// frontend/src/AppRouter.jsx - ROUTER COMPLETO ACTUALIZADO
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';

// Auth pages
import LoginPage from './pages/LoginPage';
import Register from './components/auth/Register';

// App pages existentes
import VehiclesPage from './pages/VehiclesPage';
import SensorsPage from './pages/SensorsPage';
import ReceiversPage from './pages/ReceiversPage';
import GPSPage from './pages/GPSPage';
import GeofencesPage from './pages/GeofencesPage';
import AlarmsPage from './pages/AlarmsPage';
import AlarmProfilesPage from './pages/AlarmProfilesPage';
import AlarmTicketPage from './pages/AlarmTicketPage';
import AlarmTicketDetailPage from './pages/AlarmTicketDetailPage';
import AuditPage from './pages/AuditPage';
import UsersPage from './pages/UserPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import HelpPage from './pages/HelpPage';
import NotFoundPage from './pages/NotFoundPage';
import VehicleSchematicPage from './pages/VehicleSchematicPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ClientCalculatePage from './pages/ClientCalculatePage';

// Multi-company
import JoinCompany from './components/auth/JoinCompany';

// Layout
import Layout from './components/common/Layout';
import LoadingPage from './components/common/LoadingPage';

// 🆕 E-commerce pages
import StorePage from './pages/StorePage';
import ProductDetailPage from './pages/ProductDetailPage';
import KitDetailPage from './pages/KitDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrdersPage from './pages/OrdersPage';
import AdminStorePage from './pages/AdminStorePage';
import AdminTPMSDefaultsPage from './pages/AdminTPMSDefaultsPage';
import TasksPage from './pages/TasksPage';
import TaskDetailPage from './pages/TaskDetailPage';
import TaskRulesPage from './pages/TaskRulesPage';
import ChatPage from './pages/ChatPage';
import AIAssistantPage from './pages/AIAssistantPage';
import AISettingsPage from './pages/AISettingsPage';
import ChatAnalyticsPage from './pages/ChatAnalyticsPage';
import SystemUsagePage from './pages/SystemUsagePage';
import NotificationAuditPage from './pages/NotificationAuditPage';
import PortalNotificationsPage from './pages/PortalNotificationsPage';
import TrainingPage from './pages/TrainingPage';
import TrainingCoursePage from './pages/TrainingCoursePage';
import TrainingAdminPage from './pages/TrainingAdminPage';
import AdoptionCampaignsPage from './pages/AdoptionCampaignsPage';
import OperatorsPage from './pages/OperatorsPage';

// 🆕 Billing pages
import BillingPlansPage from './pages/BillingPlansPage';
import BillingInvoicesPage from './pages/BillingInvoicesPage';
import BillingPortalPage from './pages/BillingPortalPage';
import CompaniesManagementPage from './pages/CompaniesManagementPage';
import DemoAlarmDetailPage from './pages/DemoAlarmDetailPage';
import TPMSReportsPage from './pages/TPMSReportsPage';
import TPMSActionSummaryPage from './pages/TPMSActionSummaryPage';

// -------------------- Route Guards --------------------

// Enhanced permission checker that handles superadmin
const checkPermission = (userRole, hasPermissionFunc, requiredPermission) => {
  // Superadmin can access everything
  if (userRole === 'superadmin') {
    return true;
  }

  // Admin can access everything except superadmin-only
  if (userRole === 'admin' && requiredPermission !== 'superadmin') {
    return true;
  }

  // Manager can access manager and user level
  if (userRole === 'manager' && ['manager', 'user'].includes(requiredPermission)) {
    return true;
  }

  // User can only access user level
  if (userRole === 'user' && requiredPermission === 'user') {
    return true;
  }

  // Fallback to original hasPermission function
  return hasPermissionFunc ? hasPermissionFunc(requiredPermission) : false;
};

const ProtectedRoute = ({ children, requiredPermission = null }) => {
  const { t } = useTranslation();
  const { isAuthenticated, loading, hasPermission, currentCompany, user } = useAuth();

  if (loading) return <LoadingPage message={t('common.loading')} />;

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (!currentCompany) return <LoadingPage message={t('hardcoded.router.configurando_empresa')} />;

  if (requiredPermission) {
    const userRole = currentCompany?.role || user?.role;
    const hasAccess = checkPermission(userRole, hasPermission, requiredPermission);

    if (!hasAccess) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

// Public route con soporte de invitación
const PublicRoute = ({ children }) => {
  const { t } = useTranslation();
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <LoadingPage message={t('hardcoded.router.verificando_autenticacion')} />;

  const currentUrl = window.location.href;
  const hasInviteParam = new URL(currentUrl).searchParams.has('invite');
  const hasRegistrationParam = new URL(currentUrl).searchParams.has('reg');

  if (isAuthenticated && !hasInviteParam && !hasRegistrationParam) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Semi-protegida (auth pero sin company)
const AuthenticatedRoute = ({ children }) => {
  const { t } = useTranslation();
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <LoadingPage message={t('hardcoded.router.verificando_autenticacion')} />;

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return children;
};

// 🔧 Solo admins/superadmins de Arsenal (@arsenal.mx) para la administración de tienda
const ArsenalAdminRoute = ({ children }) => {
  const { t } = useTranslation();
  const { isAuthenticated, loading, user, hasPermission, currentCompany } = useAuth();

  if (loading) return <LoadingPage message={t('hardcoded.router.verificando_permisos')} />;

  if (!isAuthenticated || !user) return <Navigate to="/login" replace />;

  const isArsenalEmail = (user.email?.toLowerCase?.() || '').endsWith('@arsenal.mx');
  const userRole = currentCompany?.role || user?.role;

  const isAdminOrSuperadmin = userRole === 'admin' || userRole === 'superadmin' || hasPermission?.('admin');

  if (!(isArsenalEmail && isAdminOrSuperadmin)) {
    return <Navigate to="/vehicles/schematics" replace />;
  }

  return children;
};

// -------------------- Main Router --------------------
const AppRouter = () => {
  const { t } = useTranslation();
  return (
    <Routes>
      {/* Public */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route path="/clientcalculate" element={<ClientCalculatePage />} />

      {/* Join Company (auth pero sin company aún) */}
      <Route
        path="/join-company"
        element={
          <AuthenticatedRoute>
            <JoinCompany />
          </AuthenticatedRoute>
        }
      />

      {/* Protegidas con Layout */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard / default */}
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Navigate to="/vehicles/schematics" replace />} />
        <Route path="vehicles/schematics" element={<VehicleSchematicPage />} />

        {/* Vehicles */}
        <Route path="vehicles" element={<VehiclesPage />} />

        {/* Sensors / Receivers */}
        <Route path="sensors" element={<SensorsPage />} />
        <Route path="receivers" element={<ReceiversPage />} />
        <Route path="gps" element={<GPSPage />} />
        <Route path="geofences" element={<GeofencesPage />} />

        {/* Alarms */}
        <Route path="alarms" element={<AlarmsPage />} />
        <Route
          path="alarm-profiles"
          element={
            <ProtectedRoute requiredPermission="manager">
              <AlarmProfilesPage />
            </ProtectedRoute>
          }
        />
        <Route path="alarm-tickets" element={<AlarmTicketPage />} />
        <Route path="alarm-tickets/:ticketId" element={<AlarmTicketDetailPage />} />
        <Route path="reports" element={<TPMSReportsPage />} />
        <Route path="reports/acciones" element={<TPMSActionSummaryPage />} />

        {/* 🆕 E-commerce - RUTAS ACTUALIZADAS */}
        <Route path="store">
          <Route index element={<StorePage />} />
          <Route path="product/:productId" element={<ProductDetailPage />} />
          <Route path="kit/:kitId" element={<KitDetailPage />} />
        </Route>
        
        {/* 🆕 NUEVA: Página dedicada de carrito */}
        <Route path="cart" element={<CartPage />} />
        
        <Route path="checkout" element={<CheckoutPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="orders/:orderId" element={<OrdersPage />} />

        {/* 🔧 Admin Store (admin y superadmin @arsenal.mx) */}
        <Route
          path="admin/store"
          element={
            <ArsenalAdminRoute>
              <AdminStorePage />
            </ArsenalAdminRoute>
          }
        />

        {/* 🔧 Defaults TPMS — Panel Arsenal */}
        <Route
          path="admin/tpms-defaults"
          element={
            <ArsenalAdminRoute>
              <AdminTPMSDefaultsPage />
            </ArsenalAdminRoute>
          }
        />

        {/* 🔧 Billing System (admin y superadmin @arsenal.mx) */}
        <Route
          path="billing/plans"
          element={
            <ArsenalAdminRoute>
              <BillingPlansPage />
            </ArsenalAdminRoute>
          }
        />
        <Route
          path="billing/invoices"
          element={
            <ArsenalAdminRoute>
              <BillingInvoicesPage />
            </ArsenalAdminRoute>
          }
        />
        <Route path="billing/portal" element={<BillingPortalPage />} />
        <Route
          path="admin/companies"
          element={
            <ArsenalAdminRoute>
              <CompaniesManagementPage />
            </ArsenalAdminRoute>
          }
        />
        <Route
          path="admin/system-usage"
          element={<ArsenalAdminRoute><SystemUsagePage /></ArsenalAdminRoute>}
        />
        <Route path="notifications" element={<PortalNotificationsPage />} />
        <Route path="training" element={<TrainingPage />} />
        <Route path="training/courses/:courseId" element={<TrainingCoursePage />} />
        <Route
          path="admin/training"
          element={<ArsenalAdminRoute><TrainingAdminPage /></ArsenalAdminRoute>}
        />
        <Route
          path="admin/notification-audit"
          element={<ArsenalAdminRoute><NotificationAuditPage /></ArsenalAdminRoute>}
        />
        <Route
          path="admin/adoption-campaigns"
          element={<ArsenalAdminRoute><AdoptionCampaignsPage /></ArsenalAdminRoute>}
        />
        <Route
          path="companies/:companyId/demo"
          element={
            <ArsenalAdminRoute>
              <DemoAlarmDetailPage />
            </ArsenalAdminRoute>
          }
        />

        {/* Users / Audit / Settings (manager, admin y superadmin) */}
        <Route
          path="users"
          element={
            <ProtectedRoute requiredPermission="manager">
              <UsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="notifications"
          element={
            <Navigate to="/profile?tab=notifications" replace />
          }
        />
        <Route
          path="audit"
          element={
            <ProtectedRoute requiredPermission="manager">
              <AuditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings"
          element={
            <ProtectedRoute requiredPermission="manager">
              <SettingsPage />
            </ProtectedRoute>
          }
        />

        {/* Operadores/Choferes — sección separada del módulo de Usuarios */}
        <Route
          path="operators"
          element={
            <ProtectedRoute>
              <OperatorsPage />
            </ProtectedRoute>
          }
        />

        {/* Módulo de Tareas APS */}
        <Route path="tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
        <Route path="tasks/rules" element={<ProtectedRoute><TaskRulesPage /></ProtectedRoute>} />
        <Route path="tasks/:taskId" element={<ProtectedRoute><TaskDetailPage /></ProtectedRoute>} />

        {/* Chat tipo Slack */}
        <Route path="chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />

        {/* Asistente APS (IA) */}
        <Route path="chat/ai" element={<ProtectedRoute><AIAssistantPage /></ProtectedRoute>} />
        <Route path="settings/ai" element={<ProtectedRoute><AISettingsPage /></ProtectedRoute>} />

        {/* Analítica de uso del Chat (empresa ve la suya; Arsenal ve global) */}
        <Route path="chat-analytics" element={<ProtectedRoute requiredPermission="manager"><ChatAnalyticsPage /></ProtectedRoute>} />

        {/* Perfil / Ayuda */}
        <Route path="profile" element={<ProfilePage />} />
        <Route path="help" element={<HelpPage />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRouter;